import { z } from 'zod';
import type { TagName } from '#dn-types/host.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import { allowedRoleIdSchema } from './allowed-role-id.ts';
import { ruleDescriptionSchema } from './description.ts';
import { portRangeSchema } from './port-range.ts';
import { protocolSchema } from './protocol.ts';

/**
 * Schema for a single inbound firewall rule as edited in a form. This is the tag/role variant (an
 * allowed role plus required tags); the route variant additionally captures a local CIDR, see
 * {@link file://./../../screens/routes/form/firewall-rule/cidr-firewall-rule-form-schema.ts}.
 *
 * Its inferred type matches the `FirewallRuleFormValues` consumed by the shared firewall-rule editing
 * components, so field-array items are compatible with them. `tags` uses `z.custom` rather than
 * `z.array(tagNameSchema)` so its input and output types are both `TagName[]` — `tagNameSchema`
 * narrows only its output (leaving `string` on the input side), which would clash with the shared
 * components when a form threads its zod *input* types (as the tag form does for config overrides).
 */
export const firewallRuleFormSchema = z.object({
  description: ruleDescriptionSchema,
  allowedRoleID: allowedRoleIdSchema,
  protocol: protocolSchema,
  tags: z.array(z.custom<TagName>((val) => tagNameSchema.safeParse(val).success)),
  portRange: portRangeSchema,
});

export type FirewallRuleFormData = z.infer<typeof firewallRuleFormSchema>;
